/**
 * Login page component for AMS/FMA
 * Provides authentication interface
 */
import { useState, useEffect } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/global/auth/AuthContext";
import { useConfigurationContext } from "@/global/ConfigurationContext";
import { Button } from "@/global/components/ui/button";
import { Input } from "@/global/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/global/components/ui/card";
import { Mail, LogIn } from "lucide-react";
import { supabase } from "@/lib/supabase";

interface LoginPageProps {
  redirectTo?: string;
  appName?: string;
}

export function LoginPage({ redirectTo = "/", appName }: LoginPageProps) {
  const {
    user,
    activeTenantId,
    login,
    selectTenant,
    loading: authLoading,
  } = useAuth();
  const { title, lightModeLogo } = useConfigurationContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [devLoginWarning, setDevLoginWarning] = useState<string | null>(null);
  const [autoRedirectAttempted, setAutoRedirectAttempted] = useState(false);
  const [autoRedirectTimeout, setAutoRedirectTimeout] = useState(false);
  const navigate = useNavigate();

  // Dev login feature flag checks
  const isDevLoginEnabled = import.meta.env.VITE_ENABLE_DEV_LOGIN === "true";
  const allowedHosts = (
    import.meta.env.VITE_DEV_LOGIN_ALLOWED_HOSTS || "localhost,127.0.0.1"
  )
    .split(",")
    .map((host: string) => host.trim().toLowerCase());
  const currentHostname = window.location.hostname.toLowerCase();
  const isHostAllowed = allowedHosts.includes(currentHostname);
  const showDevLogin = isDevLoginEnabled && isHostAllowed;

  // If user is already authenticated, handle redirection
  useEffect(() => {
    if (!authLoading && user && activeTenantId && !autoRedirectAttempted) {
      setAutoRedirectAttempted(true);
      setLoading(false);

      // If redirectTo is specified and not the default, use it
      // Otherwise, redirect to app choice page
      if (redirectTo && redirectTo !== "/") {
        navigate(redirectTo, { replace: true });
      } else {
        navigate("/app-choice", { replace: true });
      }
    }

    // Reset loading if we're not authenticated
    if (!authLoading && !user) {
      setLoading(false);
    }
  }, [
    user,
    activeTenantId,
    authLoading,
    redirectTo,
    navigate,
    autoRedirectAttempted,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await login(email, password);
      // Navigation will be handled by the useEffect above after auth state updates
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    const devEmail =
      import.meta.env.VITE_DEV_ELITE_EMAIL || "dev.elite@fma.local";
    const devPassword = import.meta.env.VITE_DEV_ELITE_PASSWORD;

    // Debug logging to verify environment variables
    console.log("Dev Login Debug:", {
      devEmail,
      hasPassword: !!devPassword,
      passwordLength: devPassword?.length || 0,
      allEnvVars: {
        VITE_ENABLE_DEV_LOGIN: import.meta.env.VITE_ENABLE_DEV_LOGIN,
        VITE_DEV_LOGIN_ALLOWED_HOSTS: import.meta.env
          .VITE_DEV_LOGIN_ALLOWED_HOSTS,
        VITE_DEV_ELITE_EMAIL: import.meta.env.VITE_DEV_ELITE_EMAIL,
        hasVITE_DEV_ELITE_PASSWORD: !!import.meta.env.VITE_DEV_ELITE_PASSWORD,
      },
    });

    if (!devPassword) {
      setDevLoginWarning(
        "VITE_DEV_ELITE_PASSWORD environment variable is not set. Please configure it in your .env file."
      );
      // Clear warning after 5 seconds
      setTimeout(() => setDevLoginWarning(null), 5000);
      return;
    }

    setLoading(true);
    setError(null);
    setDevLoginWarning(null);

    try {
      await login(devEmail, devPassword);

      // After dev login, we need to ensure the JWT token has the correct active_tenant_id
      // Simple approach: just call selectTenant which should work for the dev user
      setTimeout(async () => {
        try {
          // The dev user should have active_tenant_id in metadata
          // Call selectTenant to ensure JWT has the proper claims
          if (activeTenantId) {
            await selectTenant(activeTenantId);
          }
        } catch (refreshError) {
          console.error("Failed to refresh JWT after dev login:", refreshError);
          // If this fails, we can continue anyway - user can manually refresh
        }
      }, 1500); // Wait longer for auth state to fully update

      // Navigation will be handled by the useEffect above after auth state updates
    } catch (err) {
      console.error("Dev login error:", err);
      setError(err instanceof Error ? err.message : "Dev login failed");
      setLoading(false);
    }
  };

  const displayTitle = appName || title;

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: "linear-gradient(135deg, #0B1120 0%, #1a1f35 50%, #0B1120 100%)",
      }}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="w-full max-w-md space-y-6 relative z-10">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 border border-orange-500/30 mx-auto">
            <img
              src="/logos/Orca-logo-orange-white.png"
              alt={displayTitle}
              className="h-12 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">{displayTitle}</h1>
          <p className="text-white/60 mt-2">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <Card 
          className="border-orange-500/20 relative overflow-hidden"
          style={{
            background: "rgba(11, 17, 32, 0.8)",
            backdropFilter: "blur(20px)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 107, 53, 0.1)",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 via-transparent to-orange-500/20 pointer-events-none"></div>
          <CardHeader className="relative">
            <CardTitle className="text-white text-xl">Welcome back</CardTitle>
          </CardHeader>
          <CardContent className="relative">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/15 text-destructive text-sm p-3 rounded-md">
                  {error}
                </div>
              )}

              {devLoginWarning && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-md">
                  <strong>Dev Login Warning:</strong> {devLoginWarning}
                </div>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-orange-400 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading || authLoading}
                  className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-orange-400 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  disabled={loading || authLoading}
                  className="bg-background/30 border-border/50 text-white placeholder:text-white/60 focus:border-orange-500/50 focus:ring-orange-500/20"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border-0 shadow-lg shadow-orange-500/25"
                disabled={loading || authLoading || !email || !password}
              >
                <LogIn className="w-5 h-5 mr-2" />
                {loading || authLoading ? "Signing in..." : "Sign in"}
              </Button>

              {/* Dev Login Button - Feature Flagged */}
              {showDevLogin && (
                <div className="pt-4 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-yellow-50 border-yellow-200 text-yellow-800 hover:bg-yellow-100"
                    onClick={handleDevLogin}
                    disabled={loading || authLoading}
                  >
                    Dev Login (Elite) — FEATURE FLAG
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Hostname: {currentHostname} | Flag:{" "}
                    {isDevLoginEnabled ? "ON" : "OFF"} | Host Allowed:{" "}
                    {isHostAllowed ? "YES" : "NO"}
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-white/50 space-y-2">
          <p>
            Don't have an account?{" "}
            <Link to="/signup" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
              Create an account
            </Link>
          </p>
          <p>
            Need help?{" "}
            <a
              href="mailto:Support@findmyagency.net"
              className="text-orange-400 hover:text-orange-300 transition-colors inline-flex items-center gap-1 font-medium"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
